(() => {
  'use strict';

  const root = document.querySelector('[data-lv-cart]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const panel = root.querySelector('[data-lv-cart-dialog]');
  const body = root.querySelector('[data-lv-cart-body]');
  const itemsNode = root.querySelector('[data-lv-cart-items]');
  const emptyNode = root.querySelector('[data-lv-cart-empty]');
  const footer = root.querySelector('[data-lv-cart-footer]');
  const status = root.querySelector('[data-lv-cart-status]');
  const checkout = root.querySelector('[data-lv-cart-checkout]');
  const titleCount = root.querySelector('[data-lv-cart-title-count]');
  const routeRoot = String(root.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let currency = root.dataset.currency || 'PLN';
  let catalog = { variants: {} };
  try { catalog = JSON.parse(document.querySelector('[data-lv-cart-catalog]')?.textContent || '{}'); } catch { /* Native cart prices remain the fallback. */ }
  const variantInfo = item => catalog.variants?.[String(item.variant_id)] || null;
  const productName = item => Number(catalog.productId) > 0 && Number(item.product_id) === Number(catalog.productId)
    ? catalog.productTitle : (item.product_title || item.title || 'Produkt');
  const referencePrice = item => Math.max(Number(item.original_price ?? item.price) || 0,
    currency === catalog.currency ? Number(variantInfo(item)?.compareAtPrice) || 0 : 0);

  let currentCart = null;
  let busy = false;
  let isOpen = false;
  let returnFocus = null;
  let readVersion = 0;

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  };

  const money = (value) => {
    try {
      return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format((Number(value) || 0) / 100);
    } catch {
      return `${((Number(value) || 0) / 100).toFixed(2).replace('.', ',')} ${currency}`;
    }
  };

  const countText = (count) => {
    const n = Number(count) || 0;
    if (n === 1) return '1 produkt';
    if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return `${n} produkty`;
    return `${n} produktów`;
  };

  const setStatus = (message = '', isError = false) => {
    status.textContent = message;
    status.dataset.error = String(isError);
  };

  const setBusy = (value) => {
    busy = value;
    body.setAttribute('aria-busy', String(value));
    root.querySelectorAll('[data-lv-cart-change], [data-lv-cart-quantity]').forEach((node) => {
      node.disabled = value;
    });
    checkout.disabled = value || !currentCart?.item_count;
  };

  const safeProductUrl = (value) => {
    try {
      const url = new URL(String(value || ''), location.origin);
      return url.origin === location.origin && url.pathname.startsWith('/') ? url.pathname + url.search : null;
    } catch {
      return null;
    }
  };

  const safeImageUrl = (value) => {
    if (!value) return null;
    try {
      const url = new URL(String(value), location.origin);
      return url.protocol === 'https:' || (url.origin === location.origin && url.protocol === location.protocol) ? url.href : null;
    } catch {
      return null;
    }
  };


  const timerBar = root.querySelector('[data-lv-cart-timer-bar]');
  const timerText = root.querySelector('[data-lv-cart-timer]');
  const timerLabel = root.querySelector('[data-lv-cart-timer-label]');
  const timerIcon = root.querySelector('[data-lv-cart-timer-icon]');
  const timerKey = 'luvano-cart-countdown-v1';
  let timerDeadline = 0;
  let timerInterval = null;
  try { const saved = Number(localStorage.getItem(timerKey)); if (Number.isFinite(saved) && saved > 0) timerDeadline = saved; } catch { /* In-memory countdown works when storage is unavailable. */ }
  const tickTimer = () => {
    const seconds = Math.max(0, Math.ceil((timerDeadline - Date.now()) / 1000));
    timerText.textContent = String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0');
    timerText.hidden = seconds === 0;
    timerIcon.hidden = seconds === 0;
    timerLabel.textContent = seconds === 0 ? 'Twój koszyk nadal czeka na Ciebie' : 'Twój koszyk czeka na Ciebie';
    if (seconds === 0 && timerInterval !== null) { clearInterval(timerInterval); timerInterval = null; }
  };
  const syncTimer = count => {
    timerBar.hidden = count === 0;
    if (count === 0) {
      if (timerInterval !== null) clearInterval(timerInterval);
      timerInterval = null;
      timerDeadline = 0;
      try { localStorage.removeItem(timerKey); } catch { /* No storage required. */ }
      return;
    }
    if (!timerDeadline) {
      timerDeadline = Date.now() + 5 * 60 * 1000;
      try { localStorage.setItem(timerKey, String(timerDeadline)); } catch { /* Retain the deadline in memory. */ }
    }
    tickTimer();
    if (timerDeadline > Date.now() && timerInterval === null) timerInterval = setInterval(tickTimer, 1000);
  };

  const render = (cart) => {
    if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') throw new Error('INVALID_CART');
    currentCart = cart;
    root.dataset.empty = String(cart.item_count === 0);
    currency = cart.currency || currency;
    const fragment = document.createDocumentFragment();

    cart.items.forEach((item) => {
      const row = element('li', 'lv-cart__item');
      const media = element('div', 'lv-cart__media');
      const imageUrl = safeImageUrl(item.featured_image?.url || item.image);
      if (imageUrl) {
        const image = element('img', 'lv-cart__image');
        image.src = imageUrl;
        image.alt = item.featured_image?.alt || item.product_title || item.title || '';
        image.width = 84;
        image.height = 108;
        image.loading = 'lazy';
        media.append(image);
      } else {
        media.setAttribute('aria-hidden', 'true');
      }

      const info = element('div', 'lv-cart__item-info');
      const url = safeProductUrl(item.url);
      const name = element(url ? 'a' : 'span', 'lv-cart__item-name', productName(item));
      if (url) name.href = url;
      info.append(name);
      const offerName = variantInfo(item)?.label || item.variant_title;
      if (offerName && offerName !== 'Default Title') info.append(element('p', 'lv-cart__variant', offerName));
      if (item.selling_plan_allocation?.selling_plan?.name) info.append(element('p', 'lv-cart__variant', item.selling_plan_allocation.selling_plan.name));
      const properties = item.properties || {};
      const numbers = [...new Set(Object.keys(properties).map(key => key.match(/^(?:Kolor|Rozmiar) #(\d+)$/)?.[1]).filter(Boolean))].sort((a,b)=>Number(a)-Number(b));
      numbers.forEach(number => {
        const values = [properties['Kolor #'+number], properties['Rozmiar #'+number]].filter(value=>value!==undefined&&value!==null&&value!=='');
        if(values.length) info.append(element('p', 'lv-cart__configuration', number+'. '+values.join(' · ')));
      });
      Object.entries(properties).forEach(([key,value])=>{
        if(!key.startsWith('_')&&!/^(?:Kolor|Rozmiar) #\d+$/.test(key)&&value!==null&&value!=='') info.append(element('p','lv-cart__property',key+': '+value));
      });

      const price = element('div', 'lv-cart__prices');
      const finalPrice = Number(item.final_price ?? item.price) || 0;
      if (referencePrice(item) > finalPrice) price.append(element('s', 'lv-cart__original-price', money(referencePrice(item))));
      price.append(element('span', '', money(finalPrice)));
      info.append(price);

      const bottom = element('div', 'lv-cart__item-bottom');
      const quantity = element('div', 'lv-cart__quantity');
      const minus = element('button', '', '−');
      minus.type = 'button';
      minus.dataset.lvCartChange = item.key;
      minus.dataset.quantity = String(Math.max(0, item.quantity - 1));
      minus.dataset.focusAction = 'minus';
      minus.setAttribute('aria-label', `Zmniejsz ilość: ${item.product_title || item.title}`);

      const input = element('input');
      input.type = 'number';
      input.min = '0';
      input.max = '9999';
      input.step = '1';
      input.inputMode = 'numeric';
      input.value = String(item.quantity);
      input.dataset.lvCartQuantity = item.key;
      input.dataset.currentQuantity = String(item.quantity);
      input.dataset.focusAction = 'input';
      input.setAttribute('aria-label', `Ilość: ${item.product_title || item.title}`);

      const plus = element('button', '', '+');
      plus.type = 'button';
      plus.dataset.lvCartChange = item.key;
      plus.dataset.quantity = String(item.quantity + 1);
      plus.dataset.focusAction = 'plus';
      plus.setAttribute('aria-label', `Zwiększ ilość: ${item.product_title || item.title}`);
      quantity.append(minus, input, plus);

      const remove = element('button', 'lv-cart__remove', 'Usuń');
      remove.type = 'button';
      remove.dataset.lvCartChange = item.key;
      remove.dataset.quantity = '0';
      remove.dataset.focusAction = 'remove';
      remove.setAttribute('aria-label', `Usuń z koszyka: ${item.product_title || item.title}`);
      bottom.append(quantity, remove);
      info.append(bottom, element('p', 'lv-cart__line-total', `Suma: ${money(item.final_line_price ?? item.line_price)}`));
      row.append(media, info);
      fragment.append(row);
    });

    itemsNode.replaceChildren(fragment);
    emptyNode.hidden = cart.item_count > 0;
    itemsNode.hidden = cart.item_count === 0;
    footer.hidden = cart.item_count === 0;
    titleCount.textContent = countText(cart.item_count);
    syncTimer(cart.item_count);
    root.querySelector('[data-lv-cart-total]').textContent = money(cart.total_price);
    root.querySelector('[data-lv-cart-checkout-total]').textContent = money(cart.total_price);
    checkout.setAttribute('aria-label','Zatwierdź mój koszyk: '+money(cart.total_price)+' — przejdź do kasy');
    const savings = new Map();
    cart.items.forEach(item=>{
      const original = Number(item.original_price ?? item.price) || 0;
      const amount = Math.max(0,referencePrice(item)-original)*(Number(item.quantity)||0);
      if(amount>0){const label=variantInfo(item)?.label||'Oferta zestawu';savings.set(label,(savings.get(label)||0)+amount);}
    });
    if(Number(cart.total_discount)>0){
      const titles = new Set((cart.cart_level_discount_applications||[]).map(d=>d.title).filter(Boolean));
      cart.items.forEach(item=>(item.line_level_discount_allocations||item.discount_allocations||[]).forEach(d=>{if(d.discount_application?.title)titles.add(d.discount_application.title);}));
      const label=[...titles].join(' + ')||'Rabat naliczony w koszyku';
      savings.set(label,(savings.get(label)||0)+Number(cart.total_discount));
    }
    const discountList = root.querySelector('[data-lv-cart-discount-list]');
    const discountFragment=document.createDocumentFragment();
    savings.forEach((amount,label)=>{const line=element('li','lv-cart__discount-line');line.append(element('span','lv-cart__discount-badge',label),element('span','lv-cart__discount-amount','−'+money(amount)));discountFragment.append(line);});
    discountList.replaceChildren(discountFragment);
    root.querySelector('[data-lv-cart-discounts]').hidden=savings.size===0;
    const nativeIcon=document.querySelector('#cart-icon-bubble');
    if(nativeIcon){
      let bubble=nativeIcon.querySelector('.cart-count-bubble');
      if(!bubble&&cart.item_count>0){bubble=element('div','cart-count-bubble');const number=element('span');number.setAttribute('aria-hidden','true');bubble.append(number);nativeIcon.append(bubble);}
      if(bubble){bubble.hidden=cart.item_count===0;const number=bubble.querySelector('span[aria-hidden="true"]');if(number)number.textContent=String(cart.item_count);}
      nativeIcon.setAttribute('aria-label','Koszyk: '+countText(cart.item_count));
    }
    document.querySelectorAll('[data-lv-cart-count]').forEach((node) => {
      node.textContent = String(cart.item_count);
      node.hidden = cart.item_count === 0;
      node.setAttribute('aria-label', countText(cart.item_count));
    });
    setBusy(busy);
    window.dispatchEvent(new CustomEvent('luvano:cart-updated', { detail: cart }));
  };

  const request = async (path, data) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30000);
    const options = {
      method: data === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: controller.signal
    };
    if (data !== undefined) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(data);
    }
    try {
      let response;
      try {
        response = await fetch(routeRoot + path, options);
      } catch {
        throw new Error('NETWORK');
      }
      if (!response.ok) {
        const error = new Error('CART_HTTP');
        error.status = response.status;
        throw error;
      }
      const responseType = (response.headers.get('Content-Type') || '').split(';', 1)[0].trim().toLowerCase();
      const isJsonResponse = ['application/json', 'text/json', 'text/javascript', 'application/javascript'].includes(responseType)
        || (responseType.startsWith('application/') && responseType.endsWith('+json'));
      if (!isJsonResponse) throw new Error('INVALID_CART');
      // The successful add is confirmed by its HTTP status. The next read obtains
      // the complete cart, so an interrupted add-response body cannot invite a duplicate retry.
      if (path === 'cart/add.js') return null;
      try {
        return await response.json();
      } catch (error) {
        throw new Error(error.name === 'AbortError' ? 'NETWORK' : 'INVALID_CART');
      }
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const errorMessage = (error) => {
    if (error.status === 422) return 'Wybrana ilość jest niedostępna. Sprawdź zawartość koszyka i spróbuj ponownie.';
    if (error.status === 404) return 'Nie znaleziono wybranego produktu. Odśwież stronę i spróbuj ponownie.';
    if (error.message === 'INVALID_ITEMS') return 'Wybierz dostępny wariant produktu i jego ilość.';
    return 'Nie udało się połączyć z koszykiem. Spróbuj ponownie za chwilę.';
  };

  const readCart = async () => {
    const version = ++readVersion;
    const cart = await request('cart.js');
    if (version === readVersion) render(cart);
    return cart;
  };

  const focusFirst = () => {
    root.querySelector('.lv-cart__close')?.focus({ preventScroll: true });
  };

  const open = (opener) => {
    if (!isOpen) {
      returnFocus = opener instanceof HTMLElement ? opener : document.activeElement;
      root.hidden = false;
      isOpen = true;
      document.documentElement.classList.add('lv-cart-lock');
      focusFirst();
    }
  };

  const close = () => {
    if (!isOpen) return;
    root.hidden = true;
    isOpen = false;
    document.documentElement.classList.remove('lv-cart-lock');
    const fallback = document.querySelector('[data-lv-cart-open]');
    const focusTarget = returnFocus instanceof HTMLElement && returnFocus.isConnected && !returnFocus.matches(':disabled')
      ? returnFocus
      : fallback;
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  };

  const refresh = async () => {
    if (busy) return currentCart;
    setBusy(true);
    setStatus('Wczytywanie koszyka…');
    try {
      const cart = await readCart();
      setStatus('');
      return cart;
    } catch (error) {
      setStatus(errorMessage(error), true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const add = async (detail = {}) => {
    if (busy) {
      window.dispatchEvent(new CustomEvent('luvano:add-result', { detail: { success: false, busy: true } }));
      return;
    }
    let added = false;
    let mutationAttempted = false;
    let cart = null;
    open(detail.opener);
    setBusy(true);
    setStatus('Dodawanie produktu…');
    try {
      if (!Array.isArray(detail.items) || detail.items.length === 0) throw new Error('INVALID_ITEMS');
      const items = detail.items.map((item) => {
        const id = Number(item.id);
        const quantity = Number(item.quantity);
        if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0 || quantity > 9999) throw new Error('INVALID_ITEMS');
        const output = { id, quantity };
        if (item.properties && typeof item.properties === 'object') output.properties = item.properties;
        if (item.selling_plan) output.selling_plan = item.selling_plan;
        return output;
      });
      mutationAttempted = true;
      await request('cart/add.js', { items });
      added = true;
      cart = await readCart();
      setStatus('Produkt dodany do koszyka.');
      window.dispatchEvent(new CustomEvent('luvano:add-result', { detail: { success: true, cart } }));
    } catch (error) {
      const uncertain = mutationAttempted && !added && (error.message === 'NETWORK' || error.message === 'INVALID_CART' || error.status >= 500);
      const message = uncertain
        ? 'Nie udało się potwierdzić dodania. Sprawdź zawartość koszyka przed ponowną próbą.'
        : errorMessage(error);
      if (added) {
        setStatus('Produkt został dodany, ale nie udało się odświeżyć koszyka. Otwórz koszyk ponownie.', true);
      } else {
        // A stock-limited request can partially change a cart; reconcile it without repeating the add.
        try { cart = await readCart(); } catch { /* Preserve the original request error. */ }
        setStatus(message, true);
      }
      window.dispatchEvent(new CustomEvent('luvano:add-result', { detail: { success: added, cart, error: message, uncertain } }));
    } finally {
      setBusy(false);
    }
  };

  const change = async (key, quantity, action) => {
    if (busy || !currentCart || !Number.isInteger(quantity) || quantity < 0 || quantity > 9999) return;
    setBusy(true);
    setStatus('Aktualizowanie koszyka…');
    ++readVersion;
    try {
      const cart = await request('cart/change.js', { id: key, quantity });
      render(cart);
      setStatus(quantity === 0 ? 'Produkt usunięty z koszyka.' : 'Ilość została zaktualizowana.');
    } catch (error) {
      try { await readCart(); } catch { /* Keep the last confirmed cart visible. */ }
      setStatus(errorMessage(error), true);
    } finally {
      setBusy(false);
      if (isOpen) {
        const nextFocus = [...itemsNode.querySelectorAll('[data-focus-action]')].find((node) =>
          (node.dataset.lvCartChange || node.dataset.lvCartQuantity) === key && node.dataset.focusAction === action
        );
        (nextFocus || itemsNode.querySelector('[data-lv-cart-change]') || root.querySelector('.lv-cart__close'))?.focus({ preventScroll: true });
      }
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opener = target.closest('[data-lv-cart-open], #cart-icon-bubble, .header__icon--cart');
    if (opener) {
      event.preventDefault();
      open(opener);
      refresh();
      return;
    }
    if (!root.contains(target)) return;
    if (target.closest('[data-lv-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }
    const button = target.closest('[data-lv-cart-change]');
    if (button) {
      event.preventDefault();
      change(button.dataset.lvCartChange, Number(button.dataset.quantity), button.dataset.focusAction);
    }
  });

  root.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-lv-cart-quantity]')) return;
    const quantity = Number(input.value);
    if (input.value.trim() === '' || !Number.isInteger(quantity) || quantity < 0 || quantity > 9999) {
      input.value = input.dataset.currentQuantity;
      setStatus('Wpisz prawidłową ilość produktu.', true);
      return;
    }
    if (quantity !== Number(input.dataset.currentQuantity)) change(input.dataset.lvCartQuantity, quantity, 'input');
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = [...panel.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]')].filter((node) => node.getClientRects().length > 0);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first) {
      event.preventDefault();
      panel.focus();
    } else if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('focusin', (event) => {
    if (isOpen && !panel.contains(event.target)) focusFirst();
  });

  let checkoutSubmitting = false;
  const checkoutForm = root.querySelector('.lv-cart__checkout-form');
  checkoutForm.addEventListener('submit', async (event) => {
    if (busy || !currentCart?.item_count) { event.preventDefault(); return; }
    if (checkoutSubmitting || !window.LuvanoMetaContext) return;
    event.preventDefault();
    checkoutSubmitting = true;
    setBusy(true);
    try { await window.LuvanoMetaContext.sync(); }
    catch { /* Measurement must not prevent the customer from checking out. */ }
    finally {
      setBusy(false);
      checkoutForm.requestSubmit(event.submitter || checkout);
      checkoutSubmitting = false;
    }
  });

  window.addEventListener('luvano:add', (event) => add(event.detail || {}));
  window.LuvanoCart = { open: async (opener) => { open(opener); return refresh(); }, close, refresh };
})();
